import type { PaintedCountry, UnmappedCountry } from './types';

interface CountryRankingProps {
  countries: readonly PaintedCountry[];
  /** Reported countries no outline covers: shown, never silently dropped. */
  unmapped: readonly UnmappedCountry[];
  total: number;
  formatValue: (value: number) => string;
  contributorsLabel: string;
  outsideLabel: string;
}

/** How many rows the ranking shows before it stops. */
const VISIBLE_ROWS = 5;

/**
 * The same figures as the map, in text: a colour alone never carries a value,
 * and a reader who cannot use the map still gets the ranking.
 */
export function CountryRanking({
  countries,
  unmapped,
  total,
  formatValue,
  contributorsLabel,
  outsideLabel,
}: CountryRankingProps) {
  const rows = countries.slice(0, VISIBLE_ROWS);
  const highest = rows[0]?.value ?? 0;

  return (
    <div className="space-y-3">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">{contributorsLabel}</caption>
        <tbody>
          {rows.map((country) => (
            <tr key={country.iso2} className="align-middle">
              <th scope="row" className="py-1.5 pr-3 text-left font-medium text-foreground/90">
                {country.reportedName}
              </th>
              <td className="w-full py-1.5">
                <span className="block h-1.5 overflow-hidden rounded-full bg-muted/50">
                  <span
                    className="block h-full rounded-full"
                    style={{
                      width: `${highest > 0 ? Math.max(4, (country.value / highest) * 100) : 0}%`,
                      background: `hsl(var(--map-step-${Math.max(1, country.step)}))`,
                    }}
                  />
                </span>
              </td>
              <td className="whitespace-nowrap py-1.5 pl-3 text-right tabular-nums text-foreground/85">
                {formatValue(country.value)}
              </td>
              <td className="whitespace-nowrap py-1.5 pl-3 text-right text-xs tabular-nums text-muted-foreground">
                {total > 0 ? `${((country.value / total) * 100).toFixed(1)}%` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {unmapped.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {outsideLabel}:{' '}
          {unmapped
            .map((country) => `${country.reportedName} (${formatValue(country.value)})`)
            .join(' · ')}
        </p>
      )}
    </div>
  );
}
